import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface BlogIdeaUpdate {
  title?: string;
  description?: string;
  primary_keyword?: string;
  secondary_keywords?: string[];
}

export const useBlogIdeas = (topicalMapId: number | null) => {
  const queryClient = useQueryClient();

  // Fetch blog ideas
  const { data: blogIdeas, isLoading, refetch } = useQuery({
    queryKey: ['blog-ideas', topicalMapId],
    queryFn: async () => {
      if (!topicalMapId) return [];
      // The API paginates (PAGE_SIZE=20) but a topical map has ~40 ideas, so we
      // must follow `next` to load them all — otherwise the modal shows only the
      // first page while the card's ideas_count reports the true (larger) total.
      let url: string | null = `/api/marketing/blog-ideas/?topical_map=${topicalMapId}`;
      const all: any[] = [];
      while (url) {
        const response = await api.get(url);
        const data = response.data as any;
        if (Array.isArray(data)) {
          // Non-paginated response (defensive): take it as-is and stop.
          all.push(...data);
          break;
        }
        all.push(...(data.results ?? []));
        // DRF returns `next` as an absolute URL; reduce it to a path + query so
        // it still flows through the api client's baseURL and auth interceptors.
        url = data.next ? new URL(data.next, window.location.origin).pathname + new URL(data.next, window.location.origin).search : null;
      }
      return all;
    },
    enabled: !!topicalMapId
  });

  // Update blog idea mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: BlogIdeaUpdate }) => {
      const response = await api.patch(`/api/marketing/blog-ideas/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-ideas', topicalMapId] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to update blog idea');
    }
  });

  // Delete blog idea mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/marketing/blog-ideas/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-ideas', topicalMapId] });
      queryClient.invalidateQueries({ queryKey: ['content-stats'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to delete blog idea');
    }
  });

  // Mark as used mutation
  const markUsedMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/api/marketing/blog-ideas/${id}/mark_used/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-ideas', topicalMapId] });
      queryClient.invalidateQueries({ queryKey: ['content-stats'] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to mark blog idea as used');
    }
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, data }: { ids: number[]; data: BlogIdeaUpdate }) => {
      const response = await api.post('/api/marketing/blog-ideas/bulk-update/', {
        ids,
        ...data
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-ideas', topicalMapId] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to bulk update blog ideas');
    }
  });

  return {
    blogIdeas,
    isLoading,
    updateBlogIdea: (id: number, data: BlogIdeaUpdate) => 
      updateMutation.mutate({ id, data }),
    deleteBlogIdea: deleteMutation.mutate,
    markAsUsed: markUsedMutation.mutate,
    bulkUpdateBlogIdeas: bulkUpdateMutation.mutate,
    refetch
  };
};